import { useState } from 'react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { destructiveCopy } from '@/lib/destructiveConfirmMessages';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { useNotifications, notificationService } from './ui/NotificationSystem';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { CreditCard, DollarSign, X, Edit2, Trash2, Check, ChevronDown, ChevronUp, Info, Wallet, Plus } from 'lucide-react';
import { CustomSelect } from './ui/CustomSelect';
import { NumericInput } from './ui/NumericInput';
import { JobDTO, PaymentDTO, PaymentType, PaymentDispatcher, CompanyPaymentMethodConfigDTO } from '@/types';
import { amountDueNow, roundCurrency } from '@/lib/invoiceCalculations';
import { PaymentDetailModal } from './PaymentDetailModal';
import { usePermissions } from '@/hooks/usePermissions';
import { useJobEditPermissions } from '@/hooks/useJobEditPermissions';
import { useCompanyTimezone } from '@/hooks/useGlobalCompany';
import { useJobPayable, type Payable } from '@/hooks/usePayable';
import { formatInstantInCompanyTimeZone } from '@/lib/dateUtils';

export const PAYMENT_METHOD_LABELS: Record<PaymentType, string> = {
    Cash: 'Cash',
    CreditCard: 'Credit Card',
    Check: 'Check',
    ACHWire: 'ACH / Wire',
    ZelleVenmo: 'Zelle / Venmo',
};

interface PaymentCardProps {
    job: JobDTO;
    onJobUpdated?: (updatedJob: JobDTO) => void;
    compact?: boolean;
    /** When true, hides add/edit/delete actions */
    disabled?: boolean;
}

/** Payments card for a job. */
export function PaymentCard({ job, onJobUpdated, ...rest }: PaymentCardProps) {
    const payable = useJobPayable(job, (updatedJob) => { if (updatedJob) onJobUpdated?.(updatedJob); });
    return <PayablePaymentCard payable={payable} {...rest} />;
}

interface PayablePaymentCardProps {
    /** The job or estimate whose payments are shown (see useJobPayable / useEstimatePayable). */
    payable: Payable;
    compact?: boolean;
    /** When true, hides add/edit/delete actions */
    disabled?: boolean;
    /** Down payment layout; 'panel' is the boxed one used on invoices and estimates */
    depositVariant?: 'inline' | 'panel';
}

/** Payments card for any payable — a job or an estimate. */
export function PayablePaymentCard({ payable, compact = false, disabled = false, depositVariant = 'inline' }: PayablePaymentCardProps) {
    const { addNotification } = useNotifications();
    const companyTz = useCompanyTimezone();
    const { isFeatureAllowed } = usePermissions();
    // Admins always get access; non-admins need the payments.refund feature key
    const canViewPaymentDetails = isFeatureAllowed('payments.refund');
    const [editingPaymentId, setEditingPaymentId] = useState<string | null>(null);
    const [editPayment, setEditPayment] = useState<Partial<PaymentDTO>>({});
    const [selectedProviderPaymentId, setSelectedProviderPaymentId] = useState<string | null>(null);

    // Fetch Stripe payment details when a provider payment is selected
    const { data: stripePaymentDetails } = useQuery({
        queryKey: ['stripePayment', selectedProviderPaymentId],
        queryFn: () => apiService.getStripePayment(selectedProviderPaymentId!),
        enabled: !!selectedProviderPaymentId,
    });

    // Fetch payment method configs for the company
    const { data: paymentConfigs = [], isLoading: configsLoading } = useQuery({
        queryKey: ['paymentMethodConfigs', payable.companyId],
        queryFn: () => apiService.getPaymentMethodConfigs(payable.companyId),
        enabled: !!payable.companyId && editingPaymentId !== null,
    });

    const enabledPaymentMethods = paymentConfigs.filter((c: CompanyPaymentMethodConfigDTO) => c.is_enabled);

    const { allowedPaymentTypes } = useJobEditPermissions();
    const allowedMethods = enabledPaymentMethods.filter(
        (c: CompanyPaymentMethodConfigDTO) => allowedPaymentTypes([c.payment_method]).length > 0
    );
    const allMethodLabels = Object.entries(PAYMENT_METHOD_LABELS) as [PaymentType, string][];
    const allowedFallbackLabels = allMethodLabels.filter(([key]) => allowedPaymentTypes([key as PaymentType]).length > 0);

    // Calculate totals
    const totalPaid = payable.payments.reduce((sum, p) => sum + p.amount, 0);
    const total = payable.total;
    const remainingBalance = roundCurrency(total - totalPaid);
    const dueNow = amountDueNow(total, totalPaid, payable.depositAmount);

    // The payable updates its own caches (job lists / estimate) and notifies its owner.
    const handleMutationSuccess = (message: string) => {
        addNotification(notificationService.success(message));
        setEditingPaymentId(null);
        setEditPayment({});
    };

    // Helper to calculate payment status based on total paid vs total
    const calculatePaymentStatus = (newTotalPaid: number): 'NotPaid' | 'PartiallyPaid' | 'PaidFully' => {
        if (newTotalPaid <= 0) return 'NotPaid';
        if (newTotalPaid >= total) return 'PaidFully';
        return 'PartiallyPaid';
    };



    // Remove payment mutation - includes payment_status in the same save
    const removePaymentMutation = useMutation({
        mutationFn: async (payment: PaymentDTO) => {
            const newTotalPaid = totalPaid - payment.amount;
            await payable.savePayments([{ operation: 'Remove', payment }], calculatePaymentStatus(newTotalPaid));
        },
        onSuccess: () => handleMutationSuccess('Payment removed successfully'),
        onError: (err: any) => {
            addNotification(notificationService.error(err.message || 'Failed to remove payment'));
        },
    });

    // Edit payment mutation - includes payment_status in the same save
    const editPaymentMutation = useMutation({
        mutationFn: async ({ oldPayment, newPayment }: { oldPayment: PaymentDTO; newPayment: Partial<PaymentDTO> }) => {
            const amountDiff = (newPayment.amount || 0) - oldPayment.amount;
            const newTotalPaid = totalPaid + amountDiff;
            await payable.savePayments(
                [
                    { operation: 'Remove', payment: oldPayment },
                    { operation: 'Add', payment: newPayment as PaymentDTO }
                ],
                calculatePaymentStatus(newTotalPaid),
            );
        },
        onSuccess: () => handleMutationSuccess('Payment updated successfully'),
        onError: (err: any) => {
            addNotification(notificationService.error(err.message || 'Failed to update payment'));
        },
    });



    const handlePaymentTypeChange = (type: PaymentType) => {
        const config = paymentConfigs.find((c: CompanyPaymentMethodConfigDTO) => c.payment_method === type);
        setEditPayment(prev => ({
            ...prev,
            type,
            payment_dispatcher: config?.default_processed_by || 'Company',
        }));
    };

    const handleStartEdit = (paymentIdOrIndex: string) => {
        setEditingPaymentId(paymentIdOrIndex);
        const payment = payable.payments.find((p, i) => (p.id || `idx-${i}`) === paymentIdOrIndex);
        if (payment) {
            setEditPayment({ ...payment });
        }
    };

    const handleCancelEdit = () => {
        setEditingPaymentId(null);
        setEditPayment({});
    };

    const handleSaveEdit = (originalPayment: PaymentDTO) => {
        if (!editPayment.amount || editPayment.amount <= 0) {
            addNotification(notificationService.error('Please enter a valid amount'));
            return;
        }

        // Calculate max allowed (remaining balance + original payment amount)
        const maxAllowed = remainingBalance + originalPayment.amount;
        if (editPayment.amount > maxAllowed) {
            addNotification(notificationService.error(`Payment amount exceeds maximum of $${maxAllowed.toFixed(2)}`));
            return;
        }

        const config = paymentConfigs.find((c: CompanyPaymentMethodConfigDTO) => c.payment_method === editPayment.type);
        const feePercentage = config?.fee_percentage || 0;
        const flatFee = config?.fee_flat_amount || 0;
        const feeAmount = (editPayment.amount * (feePercentage / 100)) + flatFee;

        editPaymentMutation.mutate({
            oldPayment: originalPayment,
            newPayment: {
                ...editPayment,
                fee_amount: feeAmount,
                payment_date: editPayment.payment_date || new Date().toISOString(),
            }
        });
    };

    const handleRemovePaymentClick = (payment: PaymentDTO) => {
        setPaymentToRemove(payment);
    };

    const formatPaymentDate = (dateStr: string | undefined) => {
        if (!dateStr) return null;
        return formatInstantInCompanyTimeZone(dateStr, companyTz);
    };

    const cardClasses = compact ? 'p-3' : 'p-4';
    const titleClasses = compact ? 'text-base' : 'text-m';
    const spacingClasses = compact ? 'space-y-3' : 'space-y-3';

    const isPending = removePaymentMutation.isPending || editPaymentMutation.isPending;
    const [isExpanded, setIsExpanded] = useState(false);
    const [paymentToRemove, setPaymentToRemove] = useState<PaymentDTO | null>(null);

    // Sort payments by date descending (newest first), falling back to array order for undated entries
    const sortedPayments = [...payable.payments].sort((a, b) => {
        const dateA = a.payment_date ? new Date(a.payment_date).getTime() : 0;
        const dateB = b.payment_date ? new Date(b.payment_date).getTime() : 0;
        return dateB - dateA;
    });

    // Use the authoritative server-stored payment_status field for display.
    // Fall back to a local calculation only when the field is absent (e.g. legacy data).
    const paymentStatus = payable.paymentStatus ?? calculatePaymentStatus(totalPaid);
    const statusColor: Record<string, string> = {
        'NotPaid': 'bg-red-100 text-red-700',
        'PartiallyPaid': 'bg-orange-100 text-orange-700',
        'PaidFully': 'bg-green-100 text-green-700',
        'PaymentFailed': 'bg-red-100 text-red-700',
    };
    const statusLabel: Record<string, string> = {
        'NotPaid': 'Unpaid',
        'PartiallyPaid': 'Partial',
        'PaidFully': 'Paid',
        'PaymentFailed': 'Failed',
    };

    const removePaymentConfirmDialog = (
        <ConfirmDialog
            isOpen={!!paymentToRemove}
            onClose={() => setPaymentToRemove(null)}
            onConfirm={async () => {
                if (paymentToRemove) {
                    await removePaymentMutation.mutateAsync(paymentToRemove);
                }
            }}
            isLoading={removePaymentMutation.isPending}
            title="Remove payment?"
            message={
                <div className="space-y-2">
                    <span>{destructiveCopy.removePayment}</span>
                    <span className="block text-sm">{destructiveCopy.cannotUndo}</span>
                </div>
            }
            confirmText="Remove"
        />
    );

    // Compact mode: render content directly without collapsible wrapper
    if (compact) {
        return (
            <>
            <div className={`${cardClasses} ${spacingClasses}`}>
                <DepositRow payable={payable} totalPaid={totalPaid} remainingBalance={remainingBalance} disabled={disabled} variant={depositVariant} />

                {/* Existing payments with edit/delete */}
                {payable.payments.length > 0 && (
                    <div className={spacingClasses}>
                        {sortedPayments.map((payment, index) => {
                            const paymentKey = payment.id || `idx-${index}`;
                            const isEditing = editingPaymentId === paymentKey;

                            if (isEditing) {
                                return (
                                    <div key={payment.id || index} className="bg-white rounded-lg p-3 border-2 border-primary-300">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-sm font-medium text-gray-700">Edit Payment</span>
                                            <div className="flex gap-1">
                                                <button
                                                    type="button"
                                                    onClick={handleCancelEdit}
                                                    className="p-1 hover:bg-gray-100 rounded"
                                                    title="Cancel"
                                                >
                                                    <X className="h-4 w-4 text-gray-500" />
                                                </button>
                                            </div>
                                        </div>

                                        {configsLoading ? (
                                            <div className="flex justify-center py-4">
                                                <LoadingSpinner />
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div>
                                                        <label className="text-xs font-medium text-gray-600 mb-1 block">Method</label>
                                                        <CustomSelect
                                                            value={editPayment.type || ''}
                                                            onChange={(value) => handlePaymentTypeChange(value as PaymentType)}
                                                            options={
                                                                allowedMethods.length > 0
                                                                    ? allowedMethods.map((config: CompanyPaymentMethodConfigDTO) => ({
                                                                        value: config.payment_method,
                                                                        label: PAYMENT_METHOD_LABELS[config.payment_method]
                                                                    }))
                                                                    : allowedFallbackLabels.map(([key, label]) => ({
                                                                        value: key,
                                                                        label: label
                                                                    }))
                                                            }
                                                            placeholder="Select method"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-xs font-medium text-gray-600 mb-1 block">Amount</label>
                                                        <div className="relative">
                                                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                                                            <NumericInput
                                                                step="0.01"
                                                                min="0"
                                                                value={editPayment.amount || 0}
                                                                onChange={(val) => setEditPayment(prev => ({ ...prev, amount: val }))}
                                                                className="w-full pl-5 pr-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div>
                                                        <label className="text-xs font-medium text-gray-600 mb-1 block">Processed By</label>
                                                        <CustomSelect
                                                            value={editPayment.payment_dispatcher || 'Company'}
                                                            onChange={(value) => setEditPayment(prev => ({ ...prev, payment_dispatcher: value as PaymentDispatcher }))}
                                                            options={[
                                                                { value: 'Company', label: 'Company' },
                                                                { value: 'Technician', label: 'Technician' }
                                                            ]}
                                                            placeholder="Select processor"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-xs font-medium text-gray-600 mb-1 block">Reference</label>
                                                        <input
                                                            type="text"
                                                            value={editPayment.reference || ''}
                                                            onChange={(e) => setEditPayment(prev => ({ ...prev, reference: e.target.value }))}
                                                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                                                            placeholder="Optional"
                                                        />
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => handleSaveEdit(payment)}
                                                    disabled={isPending}
                                                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary-600 text-white text-sm font-medium rounded hover:bg-primary-700 disabled:opacity-50"
                                                >
                                                    {editPaymentMutation.isPending && <LoadingSpinner />}
                                                    <Check className="h-4 w-4" />
                                                    Save Changes
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            return (
                                <div key={payment.id || index} className="flex items-center justify-between bg-gray-50 rounded-lg p-2 border border-gray-200 group hover:border-gray-300 transition-colors">
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <div className="p-1.5 bg-gray-100 rounded flex-shrink-0">
                                            <DollarSign className="h-3.5 w-3.5 text-gray-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5">
                                                <span className="text-sm font-medium">{PAYMENT_METHOD_LABELS[payment.type] || payment.type}</span>
                                                {payment.payment_provider_payment_id && (
                                                    <span className="text-xs text-gray-400">(via Stripe)</span>
                                                )}
                                                {payment.payment_date && (
                                                    <span className="text-xs text-gray-400">{formatPaymentDate(payment.payment_date)}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {payment.amount > 0 && <span className="text-sm font-semibold text-green-600">${payment.amount.toFixed(2)}</span>}
                                        {payment.amount < 0 && <span className="text-sm font-semibold text-orange-600">${payment.amount.toFixed(2)}</span>}
                                        <div className="flex gap-1 relative z-10">
                                            {payment.payment_provider_payment_id && canViewPaymentDetails && (
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedProviderPaymentId(payment.payment_provider_payment_id!)}
                                                    className="p-1 hover:bg-blue-50 rounded text-gray-400 hover:text-blue-600"
                                                    title="View payment details"
                                                >
                                                    <Info className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                            {!disabled && (
                                                <>
                                                    {!payment.payment_provider_payment_id && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleStartEdit(paymentKey)}
                                                            disabled={isPending}
                                                            className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-primary-600 disabled:opacity-50"
                                                            title="Edit"
                                                        >
                                                            <Edit2 className="h-3.5 w-3.5" />
                                                        </button>
                                                    )}

                                                    {!payment.payment_provider_payment_id && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemovePaymentClick(payment)}
                                                            disabled={isPending}
                                                            className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-600 disabled:opacity-50"
                                                            title="Remove"
                                                        >
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}



                {payable.payments.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">No payments recorded</p>
                )}
                {/* Payment Detail Modal for provider payments */}
                {stripePaymentDetails && selectedProviderPaymentId && (
                    <PaymentDetailModal
                        payment={stripePaymentDetails}
                        onClose={() => setSelectedProviderPaymentId(null)}
                        onRefunded={payable.awaitProviderUpdate}
                    />
                )}
            </div>
            {removePaymentConfirmDialog}
            </>
        );
    }

    return (
        <div className={`bg-gray-50 rounded-lg ${isExpanded ? cardClasses : 'p-3'} transition-all duration-200`}>
            {/* Header */}
            <div
                className={`flex items-center justify-between cursor-pointer ${isExpanded ? 'mb-3' : ''}`}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center gap-4">
                    <h3 className={`${titleClasses} font-semibold text-gray-900 flex items-center gap-2`}>
                        <CreditCard className="h-5 w-5 text-green-600" />
                        Payments
                    </h3>

                    {/* Collapsed Summary */}
                    {!isExpanded && (
                        <div className="flex items-center gap-3 text-sm hidden sm:flex">
                            <div className="w-px h-5 bg-gray-300 mx-2"></div>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusColor[paymentStatus]}`}>
                                {statusLabel[paymentStatus]}
                            </span>
                            <span className="text-gray-600 font-semibold">Paid: <span className="font-medium text-green-600">${totalPaid.toFixed(2)}</span></span>
                            <span className="text-gray-600 font-semibold">Balance: <span className={`font-medium ${remainingBalance > 0 ? 'text-orange-600' : 'text-green-600'}`}>${remainingBalance.toFixed(2)}</span></span>
                            {payable.depositAmount != null && dueNow > 0 && dueNow < remainingBalance && (
                                <span className="text-gray-600 font-semibold">Down payment due: <span className="font-medium text-orange-600">${dueNow.toFixed(2)}</span></span>
                            )}
                        </div>
                    )}
                </div>

                {/* Right side - Add button and Chevron */}
                <div className="flex items-center gap-2">

                    {isExpanded ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <>
                    {/* Summary */}
                    <div className="flex justify-between items-center mb-3 pb-3 border-b border-gray-200">
                        <div className="text-sm">
                            <span className="text-gray-600">Total: </span>
                            <span className="font-medium text-gray-900">${total.toFixed(2)}</span>
                        </div>
                        <div className="text-sm">
                            <span className="text-gray-600">Paid: </span>
                            <span className="font-medium text-green-600">${totalPaid.toFixed(2)}</span>
                        </div>
                        <div className="text-sm">
                            <span className="text-gray-600">Balance: </span>
                            <span className={`font-medium ${remainingBalance > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                                ${remainingBalance.toFixed(2)}
                            </span>
                        </div>
                    </div>
                    <div className="mb-3">
                        <DepositRow payable={payable} totalPaid={totalPaid} remainingBalance={remainingBalance} disabled={disabled} />
                    </div>

                    {/* Existing payments with edit/delete */}
                    {payable.payments.length > 0 && (
                        <div className={spacingClasses}>
                            {sortedPayments.map((payment, index) => {
                                // Use payment.id if available, otherwise use index-based key
                                const paymentKey = payment.id || `idx-${index}`;
                                const isEditing = editingPaymentId === paymentKey;

                                if (isEditing) {
                                    // Edit form inline
                                    return (
                                        <div key={payment.id || index} className="bg-white rounded-lg p-3 border-2 border-primary-300">
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="text-sm font-medium text-gray-700">Edit Payment</span>
                                                <div className="flex gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={handleCancelEdit}
                                                        className="p-1 hover:bg-gray-100 rounded"
                                                        title="Cancel"
                                                    >
                                                        <X className="h-4 w-4 text-gray-500" />
                                                    </button>
                                                </div>
                                            </div>

                                            {configsLoading ? (
                                                <div className="flex justify-center py-4">
                                                    <LoadingSpinner />
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div>
                                                            <label className="text-xs font-medium text-gray-600 mb-1 block">Method</label>
                                                            <CustomSelect
                                                                value={editPayment.type || ''}
                                                                onChange={(value) => handlePaymentTypeChange(value as PaymentType)}
                                                                options={
                                                                    allowedMethods.length > 0
                                                                        ? allowedMethods.map((config: CompanyPaymentMethodConfigDTO) => ({
                                                                            value: config.payment_method,
                                                                            label: PAYMENT_METHOD_LABELS[config.payment_method]
                                                                        }))
                                                                        : allowedFallbackLabels.map(([key, label]) => ({
                                                                            value: key,
                                                                            label: label
                                                                        }))
                                                                }
                                                                placeholder="Select method"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="text-xs font-medium text-gray-600 mb-1 block">Amount</label>
                                                            <div className="relative">
                                                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                                                                <NumericInput
                                                                    step="0.01"
                                                                    min="0"
                                                                    value={editPayment.amount || 0}
                                                                    onChange={(val) => setEditPayment(prev => ({ ...prev, amount: val }))}
                                                                    className="w-full pl-5 pr-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div>
                                                            <label className="text-xs font-medium text-gray-600 mb-1 block">Processed By</label>
                                                            <CustomSelect
                                                                value={editPayment.payment_dispatcher || 'Company'}
                                                                onChange={(value) => setEditPayment(prev => ({ ...prev, payment_dispatcher: value as PaymentDispatcher }))}
                                                                options={[
                                                                    { value: 'Company', label: 'Company' },
                                                                    { value: 'Technician', label: 'Technician' }
                                                                ]}
                                                                placeholder="Select processor"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="text-xs font-medium text-gray-600 mb-1 block">Reference</label>
                                                            <input
                                                                type="text"
                                                                value={editPayment.reference || ''}
                                                                onChange={(e) => setEditPayment(prev => ({ ...prev, reference: e.target.value }))}
                                                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                                                                placeholder="Optional"
                                                            />
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSaveEdit(payment)}
                                                        disabled={isPending}
                                                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary-600 text-white text-sm font-medium rounded hover:bg-primary-700 disabled:opacity-50"
                                                    >
                                                        {editPaymentMutation.isPending && <LoadingSpinner />}
                                                        <Check className="h-4 w-4" />
                                                        Save Changes
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                }

                                // Display mode with edit/delete buttons
                                return (
                                    <div key={payment.id || index} className="flex items-center justify-between bg-white rounded-lg p-2 border border-gray-200 group hover:border-gray-300 transition-colors">
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <div className="p-1.5 bg-gray-100 rounded flex-shrink-0">
                                                <DollarSign className="h-3.5 w-3.5 text-gray-600" />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5">
                                                    <span className="text-sm font-medium">{PAYMENT_METHOD_LABELS[payment.type] || payment.type}</span>
                                                    {payment.payment_provider_payment_id && (
                                                        <span className="text-xs text-gray-400">(via Stripe)</span>
                                                    )}
                                                    {payment.payment_date && (
                                                        <span className="text-xs text-gray-400">{formatPaymentDate(payment.payment_date)}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {payment.amount > 0 && <span className="text-sm font-semibold text-green-600">${payment.amount.toFixed(2)}</span>}
                                            {payment.amount < 0 && <span className="text-sm font-semibold text-orange-600">${payment.amount.toFixed(2)}</span>}
                                            <div className="flex gap-1 relative z-10">
                                                {payment.payment_provider_payment_id && canViewPaymentDetails && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setSelectedProviderPaymentId(payment.payment_provider_payment_id!)}
                                                        className="p-1 hover:bg-blue-50 rounded text-gray-400 hover:text-blue-600"
                                                        title="View payment details"
                                                    >
                                                        <Info className="h-3.5 w-3.5" />
                                                    </button>
                                                )}
                                                {!disabled && (
                                                    <>
                                                        {!payment.payment_provider_payment_id && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleStartEdit(paymentKey)}
                                                                disabled={isPending}
                                                                className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-primary-600 disabled:opacity-50"
                                                                title="Edit"
                                                            >
                                                                <Edit2 className="h-3.5 w-3.5" />
                                                            </button>
                                                        )}
                                                        {!payment.payment_provider_payment_id && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemovePaymentClick(payment)}
                                                                disabled={isPending}
                                                                className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-600 disabled:opacity-50"
                                                                title="Remove"
                                                            >
                                                                <Trash2 className="h-3.5 w-3.5" />
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}



                    {payable.payments.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-4">No payments recorded</p>
                    )}
                </>
            )}

            {/* Payment Detail Modal for provider payments */}
            {stripePaymentDetails && selectedProviderPaymentId && (
                <PaymentDetailModal
                    payment={stripePaymentDetails}
                    onClose={() => setSelectedProviderPaymentId(null)}
                    onRefunded={payable.awaitProviderUpdate}
                />
            )}

            {removePaymentConfirmDialog}
        </div>
    );
}

/** Down payment row for a job outside its payments card (the invoice details modal). */
export function JobDownPaymentRow({ job, onJobUpdated, disabled = false }: {
    job: JobDTO;
    onJobUpdated?: (updatedJob: JobDTO) => void;
    disabled?: boolean;
}) {
    const payable = useJobPayable(job, (updatedJob) => { if (updatedJob) onJobUpdated?.(updatedJob); });
    const totalPaid = payable.payments.reduce((sum, p) => sum + p.amount, 0);
    return (
        <DepositRow
            payable={payable}
            totalPaid={totalPaid}
            remainingBalance={roundCurrency(payable.total - totalPaid)}
            disabled={disabled}
            variant="panel"
        />
    );
}

/**
 * The down payment the customer is asked for before the rest. Drives "due now" on the public page,
 * the PDF and the Take Payment default.
 */
function DepositRow({ payable, totalPaid, remainingBalance, disabled, variant = 'inline' }: {
    payable: Payable;
    totalPaid: number;
    remainingBalance: number;
    disabled: boolean;
    /** 'panel' is the boxed layout used on the invoice */
    variant?: 'inline' | 'panel';
}) {
    const { addNotification } = useNotifications();
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState<number | undefined>(undefined);
    const saveMutation = useMutation({
        mutationFn: (amount: number | null) => payable.saveDeposit(amount),
        onSuccess: () => setEditing(false),
        onError: (err: any) => {
            addNotification(notificationService.error(err.message || 'Failed to save down payment'));
        },
    });

    const deposit = payable.depositAmount;
    // Nothing to show: no deposit, and either read-only or nothing left to ask for
    if (deposit == null && (disabled || remainingBalance <= 0)) return null;

    if (variant === 'panel') {
        const quickPercents = [10, 25, 50];

        if (editing) {
            return (
                <div className="rounded-xl border border-primary-200 bg-primary-50/60 p-3 space-y-3">
                    <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary-100 shrink-0">
                            <Wallet className="h-4 w-4 text-primary-600" />
                        </div>
                        <span className="text-sm font-semibold text-gray-900">Down payment amount</span>
                    </div>
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                        <NumericInput
                            step="0.01"
                            min="0"
                            value={value}
                            onChange={setValue}
                            autoFocus
                            className="w-full pl-7 pr-3 py-2 text-base font-medium bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                    </div>
                    {payable.total > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {quickPercents.map(pct => (
                                <button
                                    key={pct}
                                    type="button"
                                    onClick={() => setValue(roundCurrency(payable.total * pct / 100))}
                                    className="px-2.5 py-1 text-xs font-medium rounded-full bg-white border border-gray-200 text-gray-700 hover:border-primary-300 hover:text-primary-700"
                                >
                                    {pct}%
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setEditing(false)}
                            className="flex-1 py-2 text-sm font-medium rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => saveMutation.mutate(value && value > 0 ? roundCurrency(value) : null)}
                            disabled={saveMutation.isPending}
                            className="flex-1 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-1.5"
                        >
                            {saveMutation.isPending ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
                            Save
                        </button>
                    </div>
                </div>
            );
        }

        if (deposit == null) {
            return (
                <button
                    type="button"
                    onClick={() => { setValue(undefined); setEditing(true); }}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-dashed border-gray-300 text-left hover:border-primary-400 hover:bg-primary-50/50 transition-colors group"
                >
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 group-hover:bg-primary-100 shrink-0">
                        <Plus className="h-4 w-4 text-gray-500 group-hover:text-primary-600" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900">Require a down payment</div>
                        <div className="text-xs text-gray-500">Ask the customer to pay part up front</div>
                    </div>
                </button>
            );
        }

        const panelDueNow = amountDueNow(payable.total, totalPaid, deposit);
        const received = panelDueNow <= 0;
        return (
            <div className={`rounded-xl border p-3 ${received ? 'border-green-200 bg-green-50/60' : 'border-orange-200 bg-orange-50/60'}`}>
                <div className="flex items-start gap-3">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${received ? 'bg-green-100' : 'bg-orange-100'}`}>
                        <Wallet className={`h-4 w-4 ${received ? 'text-green-600' : 'text-orange-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Down payment</div>
                        <div className="text-lg font-bold text-gray-900 leading-tight">${deposit.toFixed(2)}</div>
                        <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${received ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                            {received ? 'Received' : `Due now $${panelDueNow.toFixed(2)}`}
                        </span>
                    </div>
                    {!disabled && (
                        <div className="flex gap-1 shrink-0">
                            <button
                                type="button"
                                onClick={() => { setValue(deposit); setEditing(true); }}
                                disabled={saveMutation.isPending}
                                className="p-1.5 rounded-lg bg-white/70 hover:bg-white text-gray-500 hover:text-primary-600 disabled:opacity-50"
                                title="Edit down payment"
                            >
                                <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => saveMutation.mutate(null)}
                                disabled={saveMutation.isPending}
                                className="p-1.5 rounded-lg bg-white/70 hover:bg-white text-gray-500 hover:text-red-600 disabled:opacity-50"
                                title="Remove down payment"
                            >
                                {saveMutation.isPending ? <LoadingSpinner /> : <X className="h-4 w-4" />}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (editing) {
        return (
            <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-gray-600">Down payment:</span>
                <div className="relative w-28">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                    <NumericInput
                        step="0.01"
                        min="0"
                        value={value}
                        onChange={setValue}
                        autoFocus
                        className="w-full pl-5 pr-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                    />
                </div>
                <button
                    type="button"
                    onClick={() => saveMutation.mutate(value && value > 0 ? roundCurrency(value) : null)}
                    disabled={saveMutation.isPending}
                    className="p-1 hover:bg-green-50 rounded text-green-600 disabled:opacity-50"
                    title="Save down payment"
                >
                    {saveMutation.isPending ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
                </button>
                <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="p-1 hover:bg-gray-100 rounded text-gray-500"
                    title="Cancel"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>
        );
    }

    if (deposit == null) {
        return (
            <button
                type="button"
                onClick={() => { setValue(undefined); setEditing(true); }}
                className="text-sm font-medium text-primary-600 hover:underline"
            >
                + Require down payment
            </button>
        );
    }

    const dueNow = amountDueNow(payable.total, totalPaid, deposit);
    return (
        // Actions sit right after the label, not pushed to the far edge of a wide card
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span>
                <span className="text-gray-600">Down payment: </span>
                <span className="font-medium text-gray-900">${deposit.toFixed(2)}</span>
                {' · '}
                {dueNow > 0
                    ? <span className="font-medium text-orange-600">Due now ${dueNow.toFixed(2)}</span>
                    : <span className="font-medium text-green-600">Received</span>}
            </span>
            {!disabled && (
                <div className="flex gap-1">
                    <button
                        type="button"
                        onClick={() => { setValue(deposit); setEditing(true); }}
                        disabled={saveMutation.isPending}
                        className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-primary-600 disabled:opacity-50"
                        title="Edit down payment"
                    >
                        <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button
                        type="button"
                        onClick={() => saveMutation.mutate(null)}
                        disabled={saveMutation.isPending}
                        className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-600 disabled:opacity-50"
                        title="Remove down payment"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>
            )}
        </div>
    );
}
